import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { TrackCard } from '../components/TrackCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Search, Loader2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FILTER_KEYS = ['content_rating', 'voice', 'language', 'genre', 'moods', 'character'];

const FilterSection = ({ title, options, selected, onToggle, testIdPrefix }) => (
  <AccordionItem value={title} className="border-white/10">
    <AccordionTrigger className="text-white hover:no-underline py-3" data-testid={`filter-accordion-${testIdPrefix}`}>
      <span className="text-sm font-semibold">{title}</span>
    </AccordionTrigger>
    <AccordionContent>
      <div className="space-y-2 pt-1">
        {options.map((opt) => {
          const id = `${testIdPrefix}-${opt}`;
          const checked = selected.includes(opt);
          return (
            <label
              key={opt}
              htmlFor={id}
              className="flex items-center gap-2 cursor-pointer group"
              data-testid={`filter-option-${testIdPrefix}-${opt.replace(/\s+/g, '-')}`}
            >
              <Checkbox
                id={id}
                checked={checked}
                onCheckedChange={() => onToggle(opt)}
                className="border-white/20 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500 data-[state=checked]:text-slate-900"
              />
              <span className="text-sm text-slate-300 group-hover:text-white transition-colors">
                {opt}
              </span>
            </label>
          );
        })}
      </div>
    </AccordionContent>
  </AccordionItem>
);

export const MarketplacePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tracks, setTracks] = useState([]);
  const [genres, setGenres] = useState([]);
  const [vocab, setVocab] = useState({
    content_rating: [], voice: [], language: [], moods: [], character: [],
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

  // Selected filter values are arrays per key, derived from URL
  const filters = useMemo(() => {
    const out = {};
    FILTER_KEYS.forEach((k) => { out[k] = searchParams.getAll(k); });
    return out;
  }, [searchParams]);

  // Load vocab + genres once
  useEffect(() => {
    (async () => {
      try {
        const [vRes, gRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/filters/vocab`),
          axios.get(`${BACKEND_URL}/api/genres`),
        ]);
        setVocab(vRes.data);
        setGenres(gRes.data);
      } catch (e) {
        console.error('Failed to load filter vocabularies:', e);
      }
    })();
  }, []);

  // Fetch tracks whenever filters or search change
  useEffect(() => {
    const controller = new AbortController();
    const fetchTracks = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (searchQuery) params.append('search', searchQuery);
        FILTER_KEYS.forEach((k) => {
          filters[k].forEach((v) => params.append(k, v));
        });
        const response = await axios.get(`${BACKEND_URL}/api/tracks?${params.toString()}`, { signal: controller.signal });
        setTracks(response.data);
      } catch (error) {
        if (!axios.isCancel(error)) console.error('Error fetching tracks:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTracks();
    return () => controller.abort();
  }, [searchQuery, filters]);

  const toggleFilter = useCallback((key, value) => {
    const next = new URLSearchParams(searchParams);
    const current = next.getAll(key);
    next.delete(key);
    if (current.includes(value)) {
      current.filter((v) => v !== value).forEach((v) => next.append(key, v));
    } else {
      [...current, value].forEach((v) => next.append(key, v));
    }
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const clearAllFilters = () => {
    const next = new URLSearchParams();
    if (searchQuery) next.set('search', searchQuery);
    setSearchParams(next);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (searchQuery) next.set('search', searchQuery); else next.delete('search');
    setSearchParams(next);
  };

  const activeChips = FILTER_KEYS.flatMap((k) => filters[k].map((v) => ({ k, v })));

  return (
    <div className="min-h-screen bg-gradient-dark pt-24 pb-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-10">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
            Discover
          </span>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-white mb-4">
            Explore Music
          </h1>
          <p className="text-base text-slate-400 max-w-2xl">
            Find the perfect track by feel, reference, or natural language.
          </p>
        </div>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-8" data-testid="marketplace-search-form">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              type="text"
              placeholder="Search tracks or artists..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-800 border-white/10 text-white placeholder:text-slate-500 rounded-lg"
              data-testid="search-input"
            />
          </div>
          <Button type="submit" className="bg-emerald-500 text-slate-900 hover:bg-emerald-400 rounded-lg" data-testid="search-btn">
            Search
          </Button>
        </form>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
          {/* Sidebar */}
          <aside className="bg-slate-900/50 border border-white/10 rounded-xl p-5 h-fit lg:sticky lg:top-24" data-testid="marketplace-filter-sidebar">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-white tracking-wide">Filters</h2>
              <button
                onClick={clearAllFilters}
                className="text-xs text-emerald-400 hover:text-emerald-300"
                data-testid="filter-clear-all"
              >
                Clear All
              </button>
            </div>

            <Accordion type="multiple" defaultValue={['Content Rating', 'Genres', 'Moods']} className="w-full">
              <FilterSection
                title="Content Rating"
                options={vocab.content_rating}
                selected={filters.content_rating}
                onToggle={(v) => toggleFilter('content_rating', v)}
                testIdPrefix="content"
              />
              <FilterSection
                title="Voice"
                options={vocab.voice}
                selected={filters.voice}
                onToggle={(v) => toggleFilter('voice', v)}
                testIdPrefix="voice"
              />
              <FilterSection
                title="Language"
                options={vocab.language}
                selected={filters.language}
                onToggle={(v) => toggleFilter('language', v)}
                testIdPrefix="language"
              />
              <FilterSection
                title="Genres"
                options={genres}
                selected={filters.genre}
                onToggle={(v) => toggleFilter('genre', v)}
                testIdPrefix="genre"
              />
              <FilterSection
                title="Moods"
                options={vocab.moods}
                selected={filters.moods}
                onToggle={(v) => toggleFilter('moods', v)}
                testIdPrefix="moods"
              />
              <FilterSection
                title="Character"
                options={vocab.character}
                selected={filters.character}
                onToggle={(v) => toggleFilter('character', v)}
                testIdPrefix="character"
              />
            </Accordion>
          </aside>

          {/* Results */}
          <div>
            {/* Active filter chips */}
            {activeChips.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="active-filter-chips">
                {activeChips.map(({ k, v }) => (
                  <button
                    key={`${k}:${v}`}
                    onClick={() => toggleFilter(k, v)}
                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500/10 text-emerald-300 text-xs rounded-full border border-emerald-500/30 hover:bg-emerald-500/20"
                    data-testid={`filter-chip-${k}-${v.replace(/\s+/g, '-')}`}
                  >
                    {v}
                    <X className="w-3 h-3" />
                  </button>
                ))}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
              </div>
            ) : tracks.length === 0 ? (
              <div className="text-center py-20" data-testid="marketplace-empty-state">
                <p className="text-slate-400 text-lg">No tracks found matching your criteria.</p>
                <Button
                  variant="ghost"
                  onClick={clearAllFilters}
                  className="mt-4 text-emerald-400 hover:text-emerald-300"
                >
                  Clear Filters
                </Button>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-500 mb-6">{tracks.length} tracks found</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="tracks-grid">
                  {tracks.map((track) => (
                    <TrackCard key={track.id} track={track} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
